import { FunctionComponent, useContext, useEffect, useState } from "react";
import { DeliveryState } from "../../../enums/orders";
import { AuthContext } from "../../../contexts/user-context/user-context";
import { API_ENDPOINT } from "../../../configurations/global.config";
import axios from "axios";
import { useSnackBar } from "../../../contexts/snackbar";
import { Page, user_can_access_home, user_has_page_access } from "../../../utils/permissions";

const NOT_PICKED_UP_STATES: DeliveryState[] = [
  DeliveryState.CREATED,
  DeliveryState.PICKUP_REQUESTED,
  DeliveryState.PICKUP_ASSIGNED,
  DeliveryState.SCANNED_FOR_PICKUP,
  DeliveryState.PICKED_UP
];

const IGNORED_STATES: DeliveryState[] = [
  DeliveryState.DELIVERED,
  DeliveryState.CANCELLED,
  DeliveryState.DELIVERED_AT_WAREHOUSE,
];

// Define the keyframes in a style tag in your global CSS file or use CSS-in-JS library
// Alternatively, you can add this to your global stylesheet:
/*
@keyframes criticalPulse {
  0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
  70% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
  100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
}
*/


interface CriticalOrdersProps {
    onCriticalOrdersExists?: () => void;
}

const CriticalOrders: FunctionComponent<CriticalOrdersProps> = ({onCriticalOrdersExists}) => {
  const [counts, setCounts] = useState({ notPickedUp: 0, notDelivered: 0 });
  const { config, user } = useContext(AuthContext);
  const { showMessage } = useSnackBar();

  useEffect(() => {
    const getData = async () => {
      if (user_can_access_home(user) || user_has_page_access(user, Page.ORDERS)) {
        try {
          const response = await axios.get<Partial<Record<DeliveryState, number>>>(
            `${API_ENDPOINT}/v1/orders/critical/`,
            config
          );

          const data = response.data || {};
          let notPickedUp = 0;
          let notDelivered = 0;

          Object.entries(data).forEach(([state, count]) => {
            if (IGNORED_STATES.includes(state as DeliveryState)) return;

            if (NOT_PICKED_UP_STATES.includes(state as DeliveryState)) {
              notPickedUp += count || 0;
            } else {
              notDelivered += count || 0;
            }

            if (Math.max(notPickedUp, notDelivered) > 0) onCriticalOrdersExists && onCriticalOrdersExists()
          });

          setCounts({ notPickedUp, notDelivered });
        } catch {
          showMessage("Failed to get critical orders", "error");
        }
      }
    };

    getData();
  }, [user]);

  if (counts.notPickedUp === 0 && counts.notDelivered === 0) return null;

  return (
    <div style={{
      fontSize: "0.875rem",
      padding: "0.75rem",
      lineHeight: "1.5",
      borderLeft: "4px solid #ef4444",
      backgroundColor: "#fff1f2",
      borderRadius: "0.375rem",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
      position: "relative",
      animation: "pulse 2s infinite",
      width: '100%',
      maxWidth: '400px'
    }}>
      <div style={{
        position: "absolute",
        top: "0",
        left: "0",
        width: "100%",
        height: "100%",
        borderRadius: "0.375rem",
        boxShadow: "0 0 0 0 rgba(239, 68, 68, 0.7)",
        animation: "criticalPulse 2s infinite"
      }} />
      
      <div style={{ fontWeight: "600", marginBottom: "0.25rem", color: "#881337" }}>
        ⚠️ Critical Orders
      </div>
      
      {counts.notPickedUp > 0 && (
        <div style={{
          color: "#dc2626",
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          marginBottom: "0.25rem"
        }}>
          <span style={{
            display: "inline-block",
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: "#dc2626"
          }} />
          Incomplete Pickups: <span style={{ fontSize: "1.1em" }}>{counts.notPickedUp}</span>
        </div>
      )}
      
      {counts.notDelivered > 0 && (
        <div style={{
          color: "#92400e",
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem"
        }}>
          <span style={{
            display: "inline-block",
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: "#d97706"
          }} />
          Incomplete Deliveries: <span style={{ fontSize: "1.1em" }}>{counts.notDelivered}</span>
        </div>
      )}
    </div>
  );
};

export default CriticalOrders;